import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AdminList, AdminListToolbar } from "../../secondary/AdminList";
import DataTableV2, { DataTableColumn } from "../../secondary/DataTable/DataTableV2";
import { Pagination } from "../../secondary/Pagination/pagination";
import { Confirmation } from "../../secondary/Confirmation/confirmation";
import { StatusBanner } from "../../secondary/StatusBanner";
import { FormActions } from "../../secondary/FormActions/FormActions";
import { AnnouncementFormModal } from "./AnnouncementFormModal";
import { useAnnouncementList, useDeleteAnnouncementsMutation } from "../../../features/announcements";
import type { AnnouncementItem } from "../../../features/announcements";
import { userNotificationAsync } from "../../../app/userSlice";
import { useLoading } from "../../../hooks/useLoading";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";
import { LOADING_KEYS } from "../../../constants/loadingKeys";
import { LIST_VIEW_LIMIT } from "../../../constants";
import constants from "../../../constants/constants.json";
import warning from "../../../static/images/icon-check-danger.png";
import style from "./AnnouncementList.module.scss";

interface SortState {
  column: string | null;
  direction: "asc" | "desc" | null;
}

const AnnouncementList: React.FC = () => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();

  const [search, setSearch] = useState("");
  const [sortState, setSortState] = useState<SortState>({
    column: null,
    direction: null,
  });
  const [offset, setOffset] = useState(0);
  const [activePage, setActivePage] = useState(1);

  const sortType = sortState.column && sortState.direction ? `${sortState.column}:${sortState.direction.toUpperCase()}` : "";

  const { announcementList } = useAnnouncementList({
    offset,
    search,
    sortType,
  });
  const isLoading = useLoading(LOADING_KEYS.GET_ANNOUNCEMENT);
  const { mutateAsync: deleteAnnouncements, isPending: isDeleting } = useDeleteAnnouncementsMutation();

  const [selectedRows, setSelectedRows] = useState<AnnouncementItem[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [formModal, setFormModal] = useState<{
    open: boolean;
    announcementId?: number;
  }>({ open: false });

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }

    const dismissTimer = window.setTimeout(() => {
      setSuccessMessage(null);
    }, 5000);

    return () => window.clearTimeout(dismissTimer);
  }, [successMessage]);

  const canManage = hasPermission(PERMISSIONS.ADMIN_IN_APP_MESSAGING);
  const canDelete = hasPermission(PERMISSIONS.ADMIN_LIST_BULK_DELETE);
  const announcements = announcementList.data?.announcements;
  const metadata = announcementList.data?.metadata;
  const hasData = Array.isArray(announcements) && announcements.length > 0;

  const columns: DataTableColumn[] = [
    {
      Header: "Announcement",
      accessor: "title",
      fixed: hasData,
      sortable: true,
    },
    { Header: "Created date", accessor: "createdDate", sortable: true },
    { Header: "Sales office", accessor: "salesOffice", sortable: true },
  ];

  const handleSearch = (value: string) => {
    setSearch(value);
    setOffset(0);
    setActivePage(1);
    setSelectedRows([]);
  };

  const handleSearchChange = (value: string) => {
    if (value === "") {
      setSearch("");
      setOffset(0);
      setActivePage(1);
      setSortState({ column: null, direction: null });
      setSelectedRows([]);
    }
  };

  const handleSort = (column: string, direction: "asc" | "desc") => {
    setSortState({ column, direction });
    setSelectedRows([]);
  };

  const handlePageChange = (page: number) => {
    const limit = Number(LIST_VIEW_LIMIT);
    const newOffset = page <= 1 ? 0 : (page - 1) * limit;
    setOffset(newOffset);
    setActivePage(page);
    setSelectedRows([]);
  };

  const handleRowClick = (row: AnnouncementItem) => {
    if (row?.id) {
      setFormModal({ open: true, announcementId: row.id });
    }
  };

  const handleCreateClick = () => {
    setSuccessMessage(null);
    setFormModal({ open: true });
  };

  const handleFormModalClose = () => {
    setFormModal({ open: false });
  };

  const handleFormModalSuccess = (message: string) => {
    setSuccessMessage(message);
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteConfirm(false);
    await deleteAnnouncements(selectedRows.map((r) => ({ id: r.id })));
    dispatch(userNotificationAsync());
    setSelectedRows([]);
  };

  return (
    <div className={style.root}>
      <AdminList
        toolbar={
          <AdminListToolbar
            searchPlaceholder="Announcement Name, Created date, Sales office"
            searchValue={search}
            onSearchChange={handleSearchChange}
            onSearch={handleSearch}
            selectedCount={selectedRows.length}
            onDeleteSelected={canDelete ? () => setShowDeleteConfirm(true) : undefined}
            canDelete={canDelete && !isDeleting}
            onCreateClick={canManage ? handleCreateClick : undefined}
            createLabel={constants.announcement.createItem}
            canCreate={canManage}
          />
        }
      >
        {successMessage && (
          <div className={style.successBanner}>
            <StatusBanner status="success" message={successMessage} layout="inline" />
          </div>
        )}
        <DataTableV2
          key={`${offset}-${search}-${sortType}`}
          columns={columns}
          data={announcements ?? []}
          onRowClick={handleRowClick}
          rowSelect={canDelete}
          onRowSelect={(rows: AnnouncementItem[]) => setSelectedRows(rows)}
          sortBy={
            sortState.column
              ? {
                  column: sortState.column,
                  direction: sortState.direction ?? "asc",
                }
              : { column: null, direction: null }
          }
          onSort={handleSort}
          loading={isLoading}
          emptyMessage="There are no announcements in this list"
        />

        {hasData && metadata && (
          <Pagination activePage={activePage} itemsCountPerPage={metadata.limit} totalItemsCount={metadata.total} getActivePage={handlePageChange} />
        )}
      </AdminList>

      {formModal.open && (
        <AnnouncementFormModal
          open={formModal.open}
          onClose={handleFormModalClose}
          onSuccess={handleFormModalSuccess}
          announcementId={formModal.announcementId}
        />
      )}

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <Confirmation onClose={() => setShowDeleteConfirm(false)} title=" ">
          <div className="text-center pr-5 pl-5">
            <p>
              <img src={warning} alt="Warning" />
            </p>
            <p className="pb-3">
              Are you sure you&apos;d like to delete {selectedRows.length === 1 ? "this announcement" : `these ${selectedRows.length} announcements`}?
            </p>
            <FormActions
              primaryLabel="Yes"
              primaryLoading={isDeleting}
              onPrimaryClick={handleDeleteConfirm}
              secondaryLabel="No"
              secondaryVariant="outline"
              onSecondaryClick={() => setShowDeleteConfirm(false)}
            />
          </div>
        </Confirmation>
      )}
    </div>
  );
};

export default AnnouncementList;
